import { useState, useCallback, useEffect } from "react";
import { Upload, message, Button, Modal, Form } from "antd";
import type { GetProp, UploadFile, UploadProps } from "antd";
import { RcFile } from "antd/es/upload/interface";
import { InboxOutlined } from "@ant-design/icons";
import ImgCrop from "antd-img-crop";
import { isHeic } from 'heic-to';

import { useFileUpload } from "hooks/useFileUpload";
import { readAsDataUrl } from "utils/files-management";
import LoadingEllipsis from "components/loading-ellipsis/loading-ellipsis";

const { Dragger } = Upload;

type FileType = Parameters<GetProp<UploadProps, "beforeUpload">>[0];

interface UploadPhotoModalProps {
	open: boolean;
	onCancel: () => void;
	uploadMethod: (file: RcFile, thumbUrl: string) => void;
	aspectSlider?: boolean;
	disableCrop?: boolean;
}

const UploadPhotoModal = (props: UploadPhotoModalProps) => {
	const { open, onCancel, uploadMethod, aspectSlider, disableCrop } = props;
	const [fileList, setFileList] = useState<UploadFile[]>([]);
	const [loading, setLoading] = useState(false);
	const { convertHeicIfNeeded, isConverting } = useFileUpload();

	const resetState = useCallback(() => {
		setFileList([]);
	}, []);

	const handleOnClose = useCallback(() => {
		onCancel();
		resetState();
	}, [onCancel, resetState]);

	useEffect(() => {
		if (!open) {
			resetState();
		}
	}, [open]);

	const handleChange = useCallback(info => {
		const { fileList } = info;

		const { status } = info.file;
		if (status === "uploading") {
			setLoading(true);
		}

		if (status === "done") {
			message.success(`${info.file.name} file uploaded successfully.`);
			setLoading(false);
		} else if (status === "error") {
			message.error(`${info.file.name} file upload failed.`);
			setLoading(false);
		}

		setFileList(fileList.slice(-1));
	}, []);

	const handleRemove = useCallback(() => {
		setFileList([]);
	}, []);

	const customRequest = useCallback((args: any) => {
		setTimeout(() => {
			args.onSuccess?.("ok", {} as File);
		}, 0);
	}, []);

	// Converts HEIC and injects the result directly into fileList, bypassing Ant Design's
	// internal tracking so originFileObj is always the ready-to-upload file.
	const injectConvertedFile = useCallback(async (file: RcFile, uid: string) => {
		try {
			const converted = await convertHeicIfNeeded(file) as File;
			const thumbUrl = await readAsDataUrl(converted);
			setFileList([
				{
					uid,
					name: converted.name,
					status: "done",
					originFileObj: converted as RcFile,
					size: converted.size,
					type: converted.type,
					thumbUrl,
				},
			]);
		} catch {
			message.error("Failed to convert HEIC image.");
		}
	}, []);

	// For the disableCrop path: convert HEIC before Ant Design stores the file,
	// then manually inject the converted file as originFileObj.
	// Returning LIST_IGNORE prevents Ant Design from adding the original to its internal list.
	const handleBeforeUploadNoCrop = useCallback(
		async (file: RcFile): Promise<RcFile | boolean | string> => {
			if (!await isHeic(file)) {
				const thumbUrl = await readAsDataUrl(file);
				setFileList([
					{
						uid: file.uid,
						name: file.name,
						status: "done",
						originFileObj: file,
						size: file.size,
						type: file.type,
						thumbUrl,
					},
				]);
				return Upload.LIST_IGNORE;
			}

			void injectConvertedFile(file, file.uid);
			return Upload.LIST_IGNORE;
		},
		[injectConvertedFile]
	);

	// For the ImgCrop path: HEIC files can't render in the crop canvas, so intercept
	// them before ImgCrop opens. Return false to block the modal, convert in the background,
	// and inject the JPEG directly into fileList. Non-HEIC files proceed through crop normally.
	const handleBeforeCrop = useCallback(
		async (file: RcFile): Promise<boolean> => {
			if (!await isHeic(file)) return true;
			void injectConvertedFile(file, file.uid ?? `heic-${Date.now()}`);
			message.info("Cropping is not supported for HEIC or HEIF images");
			return false;
		},
		[injectConvertedFile]
	);

	const handleConfirmPhoto = useCallback(async () => {
		if (fileList.length > 0) {
			setLoading(true);

			console.log("handleConfirmPhoto fileList[0]: ", fileList[0]);

			const { originFileObj, thumbUrl = "" } = fileList[0];

			if (originFileObj) {
				await uploadMethod(originFileObj, thumbUrl);
				setLoading(false);
			}

			handleOnClose();
		}
	}, [fileList, handleOnClose, uploadMethod]);

	const onPreview = async (file: UploadFile) => {
		let src = file.url as string;
		if (!src) {
			src = await new Promise(resolve => {
				const reader = new FileReader();
				reader.readAsDataURL(file.originFileObj as FileType);
				reader.onload = () => resolve(reader.result as string);
			});
		}
		const image = new Image();
		image.src = src;
		const imgWindow = window.open(src);
		imgWindow?.document.write(image.outerHTML);
	};

	const uploadText = (loading || isConverting)
		? <LoadingEllipsis text="Loading..." />
		: <p className="ant-upload-text">Click or drag the photo to this area to upload</p>;

	return (
		<Modal
			open={open}
			onCancel={handleOnClose}
			footer={null}
			className="add-product-attachment-modal"
			destroyOnClose={true}
		>
			<Form className="add-product-form" autoComplete="off">
				<div className="file-upload-container">
					{disableCrop ? (
						<Dragger
							name={"file"}
							multiple={false}
							fileList={fileList}
							listType="picture-card"
							onChange={handleChange}
							onRemove={handleRemove}
							onPreview={onPreview}
							customRequest={customRequest}
							beforeUpload={handleBeforeUploadNoCrop}
							accept="image/*"
							className="photo-dragger"
							disabled={loading || isConverting}
						>
							<p className="ant-upload-drag-icon">
								<InboxOutlined
									style={{
										color: "#67748e"
									}}
								/>
							</p>
							{uploadText}
						</Dragger>
					) : (
						<ImgCrop
							rotationSlider
							showReset
							beforeCrop={handleBeforeCrop}
							{...(aspectSlider && {
								aspectSlider: true,
								minAspect: 0.1,
								maxAspect: 6
							})}
						>
							<Dragger
								name={"file"}
								multiple={false}
								fileList={fileList}
								listType="picture-card"
								onChange={handleChange}
								onRemove={handleRemove}
								onPreview={onPreview}
								customRequest={customRequest}
								accept="image/*"
								className="photo-dragger"
								disabled={loading || isConverting}
							>
								<p className="ant-upload-drag-icon">
									<InboxOutlined
										style={{
											color: "#67748e"
										}}
									/>
								</p>
								{uploadText}
							</Dragger>
						</ImgCrop>
					)}
					<Button
						type="primary"
						className="login-layout-button"
						onClick={handleConfirmPhoto}
						disabled={fileList.length === 0}
						loading={loading}
						style={{ marginTop: 16 }}
					>
						{loading ? "Uploading" : "Confirm"}
					</Button>
				</div>
			</Form>
		</Modal>
	);
};

export default UploadPhotoModal;
